import { useGetPartnerInvoicesQuery } from '@/api/api.slice';
import {
  formatCurrency,
  formatPaymentStatus,
} from '@/features/invoices/formattingUtils.ts';
import { InvoiceCard } from '@/features/invoices/InvoiceCard.tsx';
import {
  Invoice,
  InvoicesPaymentStateType,
} from '@/features/invoices/types.ts';
import i18n from '@/i18n/i18n';
import {
  Button,
  formatCurrencyAmount,
  Label,
  Pagination,
  PaletteUnion,
  Paper,
  TableOrCards,
  TextField,
  Typography,
  useSearchPaginationContext,
  useTableOrCardsDisplayMode,
} from '@arsnovasystems/ars-front-lib';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import { Stack } from '@mui/material';
import { createColumnHelper } from '@tanstack/react-table';
import { ChangeEvent, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Invoices screen. Imported directly from the router
 *
 * @returns JSX of the component
 */
export const Invoices = () => {
  const { t } = useTranslation();
  const searchPagination = useSearchPaginationContext();

  // eslint-disable-next-line i18next/no-literal-string
  const [displayMode, setDisplayMode] = useTableOrCardsDisplayMode('invoices');
  const { data, isLoading, isFetching } = useGetPartnerInvoicesQuery({
    paginate: searchPagination.pagination,
    search: {
      searchFullText:
        searchPagination.searchString !== '' ?
          searchPagination.searchString
        : undefined,
    },
  });

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    searchPagination.setSearchQuery(e.target.value);
  };

  const cols = useMemo(() => {
    const columnHelper = createColumnHelper<Invoice>();

    return [
      columnHelper.accessor('date', {
        id: 'data',
        header: t('ik.invoices.cols.date'),
      }),
      columnHelper.accessor('invoiceNumber', {
        id: 'invoiceNumber',
        header: t('ik.invoices.cols.invoiceNumber'),
      }),
      columnHelper.accessor(
        (info) => {
          return `${formatCurrencyAmount(info.total)} ${formatCurrency(t, info.currency)}`;
        },
        {
          id: 'total',
          header: t('ik.invoices.cols.total'),
        },
      ),
      columnHelper.accessor('paymentMode', {
        id: 'paymentMode',
        header: t('ik.invoices.cols.paymentMode'),
        cell: (info) => {
          return (
            <Label
              small
              label={t('payment.' + info.getValue())}
            />
          );
        },
      }),
      columnHelper.accessor('paymentState', {
        id: 'paymentState',
        header: t('ik.invoices.cols.paymentState'),
        cell: (info) => {
          const colorMap: Record<InvoicesPaymentStateType, PaletteUnion> = {
            unpaid: 'error',
            inprogress: 'neutral',
            paid: 'success',
          };
          const labelColor = colorMap[info.getValue()];
          return (
            <Label
              small
              color={labelColor}
              label={formatPaymentStatus(t, info.getValue())}
            />
          );
        },
      }),
      columnHelper.display({
        id: 'actions',
        meta: {
          flex: 1,
        },
        header: t('ik.table.header.actions'),
        size: 250,
        cell: (info) => {
          const urlPdf = info.row.original.urlPdf;
          return (
            <Button
              size='small'
              disabled={!urlPdf}
              onClick={() => {
                // open url in new tab
                window.open(urlPdf, '_blank');
              }}
            >
              {i18n.t('ik.see')}
            </Button>
          );
        },
      }),
    ];
  }, [t]);

  return (
    <Paper
      sx={{
        paddingBottom: 10,
        padding: 4,
        minHeight: 'calc(100% - 300px)',
      }}
    >
      <Typography
        variant={'h1'}
        sx={(theme) => ({
          color: theme.palette.neutral.dark,
        })}
      >
        {t('Invoices')}
      </Typography>
      <TableOrCards<Invoice>
        SearchComponent={
          <TextField
            label={t('Search')}
            onChange={handleInputChange}
            slotProps={{
              input: {
                endAdornment: (
                  <SearchRoundedIcon
                    sx={(theme) => ({
                      color: theme.palette.text.secondary,
                    })}
                  />
                ),
              },
            }}
            sx={{
              minWidth: '30vw',
            }}
          />
        }
        sx={{
          marginTop: 6,
        }}
        loading={isLoading}
        fetching={isFetching}
        data={data?.data ?? []}
        columns={cols}
        onDisplayModeSwitch={setDisplayMode}
        displayMode={displayMode}
        renderCard={({ row, index }) => {
          return (
            <InvoiceCard
              invoice={row}
              key={index}
            />
          );
        }}
      />

      {data ?
        <Stack sx={{ alignItems: 'center' }}>
          <Pagination
            sx={{ marginY: 6 }}
            count={data.pagination.total}
            rowsPerPage={data.pagination.limit}
            page={searchPagination.pagination.page}
            onPageChange={(_, newPage) => {
              searchPagination.handlePagination({
                page: newPage,
              });
            }}
            onRowsPerPageChange={(e) => {
              searchPagination.handlePagination({
                limit: parseInt(e.target.value),
              });
            }}
          />
        </Stack>
      : null}
    </Paper>
  );
};
